import React, { useState, useCallback, useMemo, useEffect } from "react";
import { Button, Dropdown, Input } from "@bikdotai/bik-component-library";
import { DropdownOption } from "@bikdotai/bik-component-library/dist/esm/components/dropdown/type";
import {
  ServiceScreenFeature,
  FeatureFilters,
  Pod,
  Status,
  ActivationStatus,
  TimelineFilter,
} from "../../models/serviceFeatures/ServiceScreenFeatureModel";
import { ConfirmationModal } from "../../../ui-components/confirmation-modal";
import {
  FiltersContainer,
  FiltersTitle,
  FiltersGrid,
  FilterLabel,
  TableContainer,
  FeaturesTable,
  TableHeaderRow,
  TableHeaderCell,
  TableBodyRow,
  TableCell,
  EmptyStateCell,
  FeatureNameContainer,
  ThumbnailImage,
  FeatureInfoContainer,
  FeatureName,
  NewBadge,
  FeatureDescription,
  StatusBadge,
  EnabledBadge,
  ActionsContainer,
  PaginationContainer,
  PaginationInfo,
  PaginationControls,
  PageNumber,
  LoadingContainer,
  FeatureIdCell,
  PotentialStatusBadge,
} from "./FeaturesList.styles";

interface FeaturesListProps {
  allFeatures: ServiceScreenFeature[];
  loading: boolean;
  filters: FeatureFilters;
  onFiltersChange: (filters: FeatureFilters) => void;
  onEdit: (feature: ServiceScreenFeature) => void;
  onDelete: (featureId: string) => void;
}

const FeaturesList: React.FC<FeaturesListProps> = ({
  allFeatures,
  loading,
  filters,
  onFiltersChange,
  onEdit,
  onDelete,
}) => {
  const [featureToDelete, setFeatureToDelete] =
    useState<ServiceScreenFeature | null>(null);
  const [copiedFeatureId, setCopiedFeatureId] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 50;

  // Check if feature is "new" (< 30 days old)
  const isNewFeature = (releaseDate: string): boolean => {
    try {
      const [day, month, year] = releaseDate.split("-");
      const release = new Date(
        parseInt(year),
        parseInt(month) - 1,
        parseInt(day)
      );
      const now = new Date();
      const diffTime = Math.abs(now.getTime() - release.getTime());
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays < 30;
    } catch {
      return false;
    }
  };

  // Compute potential status based on release date (only when status is Live)
  const getPotentialStatus = useCallback((feature: ServiceScreenFeature): string | null => {
    if (feature.status !== Status.Live || !feature.release_date) {
      return null;
    }

    try {
      // Convert release_date from dd-mm-yyyy to Date object
      const [day, month, year] = feature.release_date.split("-").map(Number);
      const releaseDate = new Date(year, month - 1, day);
      const currentDate = new Date();
      currentDate.setHours(0, 0, 0, 0);
      releaseDate.setHours(0, 0, 0, 0);

      // If release date is in the future
      if (releaseDate > currentDate) {
        return "coming soon";
      }

      // Calculate days difference
      const diffTime = Math.abs(currentDate.getTime() - releaseDate.getTime());
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      // If released within last 30 days
      if (diffDays < 30) {
        return "new";
      }

      // Otherwise it's live
      return "live";
    } catch {
      return null;
    }
  }, []);

  const updateFilter = (key: keyof FeatureFilters, value: any) => {
    onFiltersChange({ ...filters, [key]: value });
  };

  const handleCopyFeatureId = (featureId: string) => {
    navigator.clipboard.writeText(featureId).then(() => {
      setCopiedFeatureId(featureId);
      setTimeout(() => {
        setCopiedFeatureId(null);
      }, 2000);
    });
  };

  // Filter features based on all filter criteria
  const filteredFeatures = useMemo(() => {
    // Helper function to get timeline status for a feature
    const getTimelineStatus = (feature: ServiceScreenFeature): TimelineFilter[] => {
      // Use timeline from API if available
      if (feature.timeline) {
        // Map API timeline string to TimelineFilter enum
        const timelineMap: Record<string, TimelineFilter> = {
          "live": TimelineFilter.Live,
          "new": TimelineFilter.New,
          "coming soon": TimelineFilter.ComingSoon,
          "beta": TimelineFilter.Beta,
          "deprecated": TimelineFilter.Deprecated,
          "coming_soon_status": TimelineFilter.ComingSoonStatus,
        };
        
        const mappedTimeline = timelineMap[feature.timeline.toLowerCase()];
        if (mappedTimeline) {
          return [mappedTimeline];
        }
      }

      // Fallback: compute from status if timeline is not available
      const statuses: TimelineFilter[] = [];
      if (feature.status === Status.Beta) {
        statuses.push(TimelineFilter.Beta);
      } else if (feature.status === Status.Deprecated) {
        statuses.push(TimelineFilter.Deprecated);
      } else if (feature.status === Status.ComingSoon) {
        statuses.push(TimelineFilter.ComingSoonStatus);
      } else if (feature.status === Status.Live) {
        // For Live status, compute potential statuses
        const potentialStatus = getPotentialStatus(feature);
        if (potentialStatus === "new") {
          statuses.push(TimelineFilter.New);
        } else if (potentialStatus === "coming soon") {
          statuses.push(TimelineFilter.ComingSoon);
        } else {
          // It's live but not new or coming soon
          statuses.push(TimelineFilter.Live);
        }
      }

      return statuses;
    };

    let result = [...allFeatures];

    // Search text filter
    if (filters.searchText) {
      const searchLower = filters.searchText.toLowerCase();
      result = result.filter(
        (feature) =>
          feature.name.toLowerCase().includes(searchLower) ||
          feature.long_description.toLowerCase().includes(searchLower)
      );
    }

    // Timeline filter - use timeline field directly from API
    if (filters.timeline && filters.timeline.length > 0) {
      result = result.filter((feature) => {
        // Map TimelineFilter enum to API timeline string values
        const filterToTimelineMap: Record<TimelineFilter, string[]> = {
          [TimelineFilter.Live]: ["live"],
          [TimelineFilter.New]: ["new"],
          [TimelineFilter.ComingSoon]: ["coming soon"],
          [TimelineFilter.Beta]: ["beta"],
          [TimelineFilter.Deprecated]: ["deprecated"],
          [TimelineFilter.ComingSoonStatus]: ["coming_soon_status"],
        };
        
        // Use timeline from API if available
        if (feature.timeline) {
          const featureTimeline = feature.timeline.toLowerCase();
          return filters.timeline!.some((filterStatus) => {
            const apiTimelineValues = filterToTimelineMap[filterStatus] || [];
            return apiTimelineValues.includes(featureTimeline);
          });
        }
        
        // Fallback: compute from status if timeline is not available
        const timelineStatuses = getTimelineStatus(feature);
        return filters.timeline!.some((filterStatus) =>
          timelineStatuses.includes(filterStatus)
        );
      });
    }

    // Pod filter
    if (filters.pod && filters.pod.length > 0) {
      result = result.filter((feature) =>
        filters.pod!.includes(feature.pod)
      );
    }

    // Channel support filter
    if (filters.channelSupport && filters.channelSupport.length > 0) {
      result = result.filter((feature) =>
        filters.channelSupport!.some((channel) =>
          feature.channels_supported.includes(channel)
        )
      );
    }

    // Business goal filter
    if (filters.businessGoal && filters.businessGoal.length > 0) {
      result = result.filter((feature) =>
        feature.goals &&
        filters.businessGoal!.some((goal) => feature.goals!.includes(goal))
      );
    }

    // Activation status filter
    if (filters.activationStatus && filters.activationStatus.length > 0) {
      result = result.filter((feature) => {
        const isEnabled = filters.activationStatus!.includes(
          ActivationStatus.Enabled
        );
        const isNotEnabled = filters.activationStatus!.includes(
          ActivationStatus.NotEnabled
        );
        if (isEnabled && isNotEnabled) return true;
        if (isEnabled) return feature.enable === true;
        if (isNotEnabled) return feature.enable === false;
        return false;
      });
    }

    return result;
  }, [allFeatures, filters, getPotentialStatus]);

  // Paginate filtered features
  const paginatedFeatures = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredFeatures.slice(startIndex, endIndex);
  }, [filteredFeatures, currentPage, itemsPerPage]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filters]);

  // Calculate pagination info
  const totalCount = filteredFeatures.length;
  const totalPages = Math.ceil(totalCount / itemsPerPage);
  const hasNext = currentPage < totalPages;
  const hasPrevious = currentPage > 1;

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  // Dropdown options for filters
  const timelineOptions: DropdownOption[] = [
    {
      label: "New",
      value: TimelineFilter.New,
      selected: filters.timeline?.includes(TimelineFilter.New),
    },
    {
      label: "Coming Soon",
      value: TimelineFilter.ComingSoon,
      selected: filters.timeline?.includes(TimelineFilter.ComingSoon),
    },
    {
      label: "Beta",
      value: TimelineFilter.Beta,
      selected: filters.timeline?.includes(TimelineFilter.Beta),
    },
    {
      label: "Live",
      value: TimelineFilter.Live,
      selected: filters.timeline?.includes(TimelineFilter.Live),
    },
    {
      label: "Coming Soon (Status)",
      value: TimelineFilter.ComingSoonStatus,
      selected: filters.timeline?.includes(TimelineFilter.ComingSoonStatus),
    },
    {
      label: "Deprecated",
      value: TimelineFilter.Deprecated,
      selected: filters.timeline?.includes(TimelineFilter.Deprecated),
    },
  ];

  const podOptions: DropdownOption[] = Object.values(Pod).map((pod) => ({
    label: pod,
    value: pod,
    selected: filters.pod?.includes(pod),
  }));

  const activationStatusOptions: DropdownOption[] = [
    {
      label: ActivationStatus.Enabled,
      value: ActivationStatus.Enabled,
      selected: filters.activationStatus?.includes(ActivationStatus.Enabled),
    },
    {
      label: ActivationStatus.NotEnabled,
      value: ActivationStatus.NotEnabled,
      selected: filters.activationStatus?.includes(ActivationStatus.NotEnabled),
    },
  ];

  if (loading) {
    return (
      <LoadingContainer>
        <p>Loading features...</p>
      </LoadingContainer>
    );
  }

  return (
    <div>
      {/* Filters */}
      <FiltersContainer>
        <FiltersTitle>Filters</FiltersTitle>
        <FiltersGrid>
          <div>
            <FilterLabel>Search</FilterLabel>
            <Input
              value={filters.searchText || ""}
              onChangeText={(value) => updateFilter("searchText", value)}
              placeholder="Search features..."
            />
          </div>

          <div>
            <FilterLabel>Timeline</FilterLabel>
            <Dropdown
              options={timelineOptions}
              isMultiSelect={true}
              onSelect={(options: any) => {
                if (Array.isArray(options)) {
                  const selected = options
                    .filter((o: any) => o.selected)
                    .map((o: any) => o.value as TimelineFilter);
                  updateFilter(
                    "timeline",
                    selected.length > 0 ? selected : undefined
                  );
                }
              }}
              width="100%"
            />
          </div>

          <div>
            <FilterLabel>Pod</FilterLabel>
            <Dropdown
              options={podOptions}
              isMultiSelect={true}
              onSelect={(options: any) => {
                if (Array.isArray(options)) {
                  const selected = options
                    .filter((o: any) => o.selected)
                    .map((o: any) => o.value as Pod);
                  updateFilter(
                    "pod",
                    selected.length > 0 ? selected : undefined
                  );
                }
              }}
              width="100%"
            />
          </div>

          <div>
            <FilterLabel>Activation Status</FilterLabel>
            <Dropdown
              options={activationStatusOptions}
              isMultiSelect={true}
              onSelect={(options: any) => {
                if (Array.isArray(options)) {
                  const selected = options
                    .filter((o: any) => o.selected)
                    .map((o: any) => o.value);
                  updateFilter(
                    "activationStatus",
                    selected.length > 0 ? selected : undefined
                  );
                }
              }}
              width="100%"
            />
          </div>
        </FiltersGrid>
      </FiltersContainer>

      {/* Features Table */}
      <TableContainer>
        <FeaturesTable>
          <thead>
            <TableHeaderRow>
              <TableHeaderCell>Name</TableHeaderCell>
              <TableHeaderCell>Feature ID</TableHeaderCell>
              <TableHeaderCell>Pod</TableHeaderCell>
              <TableHeaderCell>Status</TableHeaderCell>
              <TableHeaderCell>Importance</TableHeaderCell>
              <TableHeaderCell>Release Date</TableHeaderCell>
              <TableHeaderCell>Enabled</TableHeaderCell>
              <TableHeaderCell>Visible</TableHeaderCell>
              <TableHeaderCell textAlign="center">Actions</TableHeaderCell>
            </TableHeaderRow>
          </thead>
          <tbody>
            {paginatedFeatures.length === 0 ? (
              <tr>
                <EmptyStateCell colSpan={9}>No features found</EmptyStateCell>
              </tr>
            ) : (
              paginatedFeatures.map((feature) => (
                <TableBodyRow key={feature.feature_id}>
                  <TableCell>
                    <FeatureNameContainer>
                      {feature.thumbnail_url && (
                        <ThumbnailImage
                          src={feature.thumbnail_url}
                          alt={feature.name}
                        />
                      )}
                      <FeatureInfoContainer>
                        <FeatureName>
                          {feature.name}
                          {isNewFeature(feature.release_date) && (
                            <NewBadge>NEW</NewBadge>
                          )}
                        </FeatureName>
                        <FeatureDescription>
                          {feature.short_description}
                        </FeatureDescription>
                      </FeatureInfoContainer>
                    </FeatureNameContainer>
                  </TableCell>
                  <FeatureIdCell
                    onDoubleClick={() =>
                      handleCopyFeatureId(feature.feature_id)
                    }
                    title="Double-click to copy"
                  >
                    {copiedFeatureId === feature.feature_id ? (
                      <span style={{ color: "#10b981" }}>Copied!</span>
                    ) : (
                      feature.feature_id
                    )}
                  </FeatureIdCell>
                  <TableCell>{feature.pod}</TableCell>
                  <TableCell>
                    {(() => {
                      // Use timeline from API if available
                      if (feature.timeline) {
                        const timeline = feature.timeline.toLowerCase();
                        // Map API timeline values to potential status display
                        if (timeline === "new" || timeline === "coming soon") {
                          return (
                            <PotentialStatusBadge statusType={timeline}>
                              {timeline.toUpperCase()}
                            </PotentialStatusBadge>
                          );
                        }
                        // For other timeline values, show status badge
                        return (
                          <StatusBadge status={feature.status}>
                            {feature.status}
                          </StatusBadge>
                        );
                      }
                      
                      // Fallback: compute potential status if timeline not available
                      const potentialStatus = getPotentialStatus(feature);
                      if (potentialStatus) {
                        return (
                          <PotentialStatusBadge statusType={potentialStatus}>
                            {potentialStatus.toUpperCase()}
                          </PotentialStatusBadge>
                        );
                      }
                      return (
                        <StatusBadge status={feature.status}>
                          {feature.status}
                        </StatusBadge>
                      );
                    })()}
                  </TableCell>
                  <TableCell textTransform="uppercase">
                    {feature.feature_importance}
                  </TableCell>
                  <TableCell>{feature.release_date}</TableCell>
                  <TableCell>
                    <EnabledBadge enabled={feature.enable}>
                      {feature.enable ? "Yes" : "No"}
                    </EnabledBadge>
                  </TableCell>
                  <TableCell>
                    <EnabledBadge enabled={feature.visible}>
                      {feature.visible ? "Yes" : "No"}
                    </EnabledBadge>
                  </TableCell>
                  <TableCell textAlign="center">
                    <ActionsContainer>
                      <Button
                        buttonText="Edit"
                        onClick={() => onEdit(feature)}
                        size="small"
                        style={{
                          backgroundColor: "#731DCF",
                          color: "white",
                          fontSize: "12px",
                        }}
                      />
                      <Button
                        buttonText="Delete"
                        onClick={() => setFeatureToDelete(feature)}
                        size="small"
                        style={{
                          backgroundColor: "#dc2626",
                          color: "white",
                          fontSize: "12px",
                        }}
                      />
                    </ActionsContainer>
                  </TableCell>
                </TableBodyRow>
              ))
            )}
          </tbody>
        </FeaturesTable>
      </TableContainer>

      {/* Pagination */}
      <PaginationContainer>
        <PaginationInfo>
          Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
          {Math.min(currentPage * itemsPerPage, totalCount)} of {totalCount}{" "}
          features
        </PaginationInfo>
        <PaginationControls>
          <Button
            buttonText="Previous"
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={!hasPrevious}
            size="small"
            style={{
              backgroundColor: hasPrevious ? "#731DCF" : "#d1d5db",
              color: "white",
            }}
          />
          <PageNumber>Page {currentPage}</PageNumber>
          <Button
            buttonText="Next"
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={!hasNext}
            size="small"
            style={{
              backgroundColor: hasNext ? "#731DCF" : "#d1d5db",
              color: "white",
            }}
          />
        </PaginationControls>
      </PaginationContainer>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        open={featureToDelete !== null}
        title="Delete Feature"
        subTitle={`Are you sure you want to delete "${featureToDelete?.name}"? This action cannot be undone.`}
        positiveButtonText="Delete"
        negativeButtonText="Cancel"
        onPositiveClick={() => {
          if (featureToDelete) {
            onDelete(featureToDelete.feature_id);
            setFeatureToDelete(null);
          }
        }}
        onNegativeClick={() => setFeatureToDelete(null)}
        onCloseClick={() => setFeatureToDelete(null)}
      />
    </div>
  );
};

export default FeaturesList;
